import { useContext, useRef, useState, useLayoutEffect } from 'react';
import type { PropsWithChildren, RefObject } from 'react';
import { createStyles } from '@mantine/core';

import AppBanners from '~/domains/common/components/AppBanners/AppBanners';
import { CHAT_WIDGET_HEIGHT } from '~/domains/design-system/components/Chat/ChatWidget/constants';

import GamecenterHeader from '../GamecenterHeader/GamecenterHeader';
import { useContestContext } from '../../context/ContestContext/ContestContext';
import GamecenterPrevPathContext from '../../context/GamecenterPrevPathContext/GamecenterPrevPathContext';
import { useChatWidgetVisible } from '../../hooks/useChatWidgetVisible';
import GamecenterLayoutWithChat, {
  type ChatLayoutMode,
} from '../GamecenterLayoutWithChat/GamecenterLayoutWithChat';
import { ContestChatPanel } from '../ContestChat/ContestChatPanel';

import GamecenterNonScheduledNav from './GamecenterNonScheduledNav/GamecenterNonScheduledNav';

const useStyles = createStyles(
  (theme, { isFlex, showChatWidget }: { isFlex: boolean; showChatWidget: boolean }) => ({
    headerContainer: {
      position: 'sticky',
      top: 0,
      zIndex: theme.other.zIndex.header,
    },
    container: {
      paddingBottom: showChatWidget ? CHAT_WIDGET_HEIGHT.SAFE_AREA : 0,
      ...(isFlex
        ? {
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100svh',
          }
        : {}),
    },
  })
);

function GamecenterNonScheduledLayout({
  children,
  headerSlot,
  isFlex,
  headerRef: externalHeaderRef,
  chatLayoutMode,
}: PropsWithChildren<{
  headerSlot?: JSX.Element;
  isFlex?: boolean;
  headerRef?: RefObject<HTMLDivElement>;
  chatLayoutMode?: ChatLayoutMode;
}>) {
  const showChatWidget = useChatWidgetVisible();
  const { classes } = useStyles({ isFlex: !!isFlex, showChatWidget });
  const { contest } = useContestContext();
  const { prevLocationBeforeGamecenter } = useContext(GamecenterPrevPathContext);
  const internalHeaderRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number | undefined>(undefined);

  const headerRef = externalHeaderRef ?? internalHeaderRef;

  useLayoutEffect(() => {
    const measureHeader = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    measureHeader();

    const resizeObserver = new ResizeObserver(measureHeader);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [headerRef]);

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer} ref={headerRef}>
        <GamecenterHeader closeHref={prevLocationBeforeGamecenter} contest={contest} />
        <GamecenterNonScheduledNav contest={contest} />
        <AppBanners />
        {headerSlot}
      </div>
      <GamecenterLayoutWithChat
        chatSlot={<ContestChatPanel />}
        layoutMode={chatLayoutMode}
        headerHeight={headerHeight}
      >
        {children}
      </GamecenterLayoutWithChat>
    </div>
  );
}

export default GamecenterNonScheduledLayout;
